import { useContext, useEffect, useRef, useState } from 'react'
import { Avatar } from '@mui/material'
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined'
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined'

import defaultAvatar from '../../../../icon/defaultAvatars/user.png'

import AiMarkdown from '../../components/AiMarkdown'
import ParticipationCard, { PendingLine } from './ParticipationCard'
import { buildCcContext } from './ccContext'
import { countWords, formatCount, useChatInputTelemetry } from './useChatInputTelemetry'
import { retryDelay } from '../probeService'

import { CodeContext } from '../../context/CodeContext'
import { TerminalContext } from '../../context/TerminalContext'
import { TelemetryContext } from '../../context/TelemetryContext'
import { useRegion } from '../../hooks/useRegion'

import {
  ccFailureOf,
  getCcState,
  postCcCancel,
  postCcChecker,
  postCcContribution,
  postCcRendered,
  postCcRequest,
} from '../../../../service/ccTutorService'
import { newClientRequestId } from '../../../../service/studentChatService'

import { CC_EVENTS, CHAT_EVENTS, REGIONS } from '../../constants/telemetryEvents'

import '../../css/workspace.scss'
import './cc-chat.scss'

// The chat column of the Code Coach substudy's conditions (CC-O observe, CC-M mediate), in place of
// ChatWindow (Workspace.jsx; studyConfig.isCcStudy). It looks like the regular AI chat, with two AI
// roles under functional names instead of one assistant - "Code Coach" and "Evidence Checker", each
// with an icon of what it does in place of the assistant's picture (RoleAvatar) - and the
// disclosure line above them. Everything about a help round is decided on the
// server (backend ccTutor, ccTutorService.js): the page sends the student's question, or their
// answer to a "Your prediction" card, with their code and latest run (ccContext.js), and shows
// what comes back. The page never knows the arm: a card appears only when a reply brings its
// prompt, which only a mediate round within its opportunities does.
//
// The editor and Run Tests stay usable throughout, a card included. A card waits, with nothing
// timing out, until the student answers it (its own box, or any message sent from the chat box),
// skips it ("I'm not sure—continue"), or starts a new question (which cancels its round); a reload
// brings it back from the server. Once the coding time is up the column goes (Workspace's study
// phase), so nothing arrives during the probes; a reply the server refuses for that (coding-over)
// closes the chat too.
//
// The conversation itself is the server's: the page keeps in step with it. A reload while a round
// is being answered, or a call whose answer never came back, can leave the page behind, and it
// then looks at the server's state again until it has caught up.
//
// Each coach and checker message, and the card, is acknowledged to the server once it has been on
// screen (POST rendered) - evidence that it was shown, not that it was read.

export const CC_DISCLOSURE =
  'Code Coach suggests a next step. Evidence Checker reviews the available evidence. Both are AI roles and can make mistakes.'

const ROLE_LABELS = { coach: 'Code Coach', checker: 'Evidence Checker' }

// Each AI role's picture (the research lead, 2026-11-02): a symbol of what the role does - a next
// step, a check of the evidence - in the regular chat's avatar circle, never a face or a persona
// (the substudy's specification: functional role labels, not gendered identities, faces or
// personalities). The name under it says who it is, so the icon is decorative.
const ROLE_ICONS = {
  coach: { Icon: LightbulbOutlined, color: '#1976d2' },
  checker: { Icon: FactCheckOutlined, color: '#5e35b1' },
}

function RoleAvatar({ role }) {
  const { Icon, color } = ROLE_ICONS[role]
  return (
    <Avatar className={`cc-role-avatar cc-role-avatar-${role}`} sx={{ bgcolor: color, width: 38, height: 38 }}>
      <Icon sx={{ fontSize: '1.2rem' }} />
    </Avatar>
  )
}

const FAILED_TEXT = 'The tutor could not answer just now. Please try again.'
const ANSWER_FAILED_TEXT = 'The tutor could not take your answer just now. Please send it again.'
const BUSY_TEXT = 'The tutor is still working on an earlier message. Please try again in a moment.'
const NOT_STARTED_TEXT = 'The exercise has not started yet. Please try again once it has.'
const CANCEL_FAILED_TEXT = 'Could not start a new question just now. Please try again.'
const REFUSED_TEXT = 'The tutor could not take this message.'
const TOO_LONG_TEXT = 'This message is too long for the tutor. Shorten it and send it again.'
const WAITING_CARD_TEXT = 'An earlier question is still waiting for your prediction. Answer it, or start a new question.'
const CODING_OVER_TEXT = 'The coding time is over, so the tutor no longer answers.'
const CHAT_OFF_TEXT = 'The AI chat is turned off for this session.'
const UNAVAILABLE_TEXT = 'The tutor is not available on this page.'

// How often a render acknowledgement the server did not take is sent again.
const RENDERED_ATTEMPTS = 4
// How many more looks the page takes at a round the server is still answering (answeringOf):
// about two minutes on retryDelay's schedule, longer than the server gives a round's model calls.
const FOLLOW_ATTEMPTS = 8

// The page's own copy of the student's message until the server names it.
const LOCAL = 'local:'
const localId = (clientRequestId) => `${LOCAL}${clientRequestId}`
// The card's id in the render acknowledgements (the server's message ids are '<roundId>:<role>').
const promptId = (roundId) => `${roundId}:prompt`

// "10:30 AM", as ChatWindow shows a message's time.
const formatTime = (at) => {
  if (at == null || at === '') return ''
  const date = new Date(at)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// The messages with `incoming` in: one already there (the same id) is replaced where it stands, a
// new one goes at the end.
const mergeMessages = (list, incoming) => {
  const next = [...list]
  incoming.forEach((message) => {
    const index = next.findIndex((existing) => existing.id === message.id)
    if (index === -1) next.push(message)
    else next[index] = message
  })
  return next
}

// The page's copy of a message, under the id the server gave it (or dropped, when the server's own
// copy is there already).
const rekey = (list, fromId, toId, roundId) =>
  list.some((message) => message.id === toId)
    ? list.filter((message) => message.id !== fromId)
    : list.map((message) => (message.id === fromId ? { ...message, id: toId, roundId } : message))

// Whether a look at the server's state brought nothing new: then what is shown stays as it is, and
// the history stays scrolled where the student left it.
const sameMessages = (a, b) =>
  a.length === b.length && a.every((message, i) => message.id === b[i].id && message.text === b[i].text && message.at === b[i].at)
const sameCard = (a, b) => a === b || (Boolean(a) && Boolean(b) && a.roundId === b.roundId && a.prompt === b.prompt)
const sameAnswering = (a, b) => Boolean(a) && Boolean(b) && a.roundId === b.roundId && a.role === b.role && a.sure === b.sure

// The student's latest round while the server is still answering it, as the server's state shows
// it: { roundId, role, sure }, or null - during the coding time only. The server names the round
// (generating). One that does not say goes by the round's last message, with no card waiting.
// Ending with the student's question, the round is with the Code Coach; ending with their answer
// to a card, with the Evidence Checker - sure, as a call that failed would have left a notice
// after them. Ending with the coach's message, it may be with the checker, or it may be finished
// (a platform question's answer, a card cancelled), which the messages do not tell apart: not sure.
const answeringOf = ({ messages, pending, generating, codingOver }) => {
  if (codingOver) return null
  if (generating !== undefined) return generating && { roundId: generating.roundId, role: generating.role, sure: true }
  if (pending || !messages.length) return null
  const last = messages[messages.length - 1]
  if (last.role === 'student') return { roundId: last.roundId, role: 'coach', sure: true }
  if (last.role === 'contribution') return { roundId: last.roundId, role: 'checker', sure: true }
  if (last.role === 'coach') return { roundId: last.roundId, role: 'checker', sure: false }
  return null
}

// The student's latest round when its Evidence Checker did not answer, as the server's state shows
// it: the server's notice after the Code Coach's message (a round whose coach failed has no coach
// message, and is asked again as a new question). Only the latest round's checker can be asked
// again. { roundId, noticeId, text }, or null.
const failedCheckerOf = ({ messages, codingOver }) => {
  if (codingOver || !messages.length) return null
  const last = messages[messages.length - 1]
  if (last.role !== 'notice' || last.roundId == null) return null
  const coached = messages.some((message) => message.roundId === last.roundId && message.role === 'coach')
  return coached ? { roundId: last.roundId, noticeId: last.id, text: last.text || FAILED_TEXT } : null
}

// Why a failed call means the chat takes no more messages on this page, or null: the coding time
// is over, the AI chat is off (End Session), the substudy is switched off, or this sign-in or this
// exercise has no tutor here (staff opening the condition, say, a server without the substudy's
// routes, or an exercise the Code Coach is not set up for: 409 cc-not-configured, or a 409
// cc-unavailable - a 502 of that reason is a reply that failed, which Try again asks for again).
// A round's own call answering 404 only means that round is gone, which a look at the server's
// state shows; the state itself has no round to have moved on, so a 409 to it closes the chat
// too, but for not-started.
const closingText = (failure, stage) => {
  if (failure.reason === 'coding-over') return CODING_OVER_TEXT
  if (failure.reason === 'chat-off') return CHAT_OFF_TEXT
  if (failure.reason === 'cc-disabled' || failure.status === 403) return UNAVAILABLE_TEXT
  if (failure.reason === 'cc-not-configured') return UNAVAILABLE_TEXT
  if (failure.status === 409 && failure.reason === 'cc-unavailable') return UNAVAILABLE_TEXT
  if (failure.status === 404 && (stage === 'state' || stage === 'request')) return UNAVAILABLE_TEXT
  if (failure.status === 409 && stage === 'state' && failure.reason !== 'not-started') return UNAVAILABLE_TEXT
  return null
}

// Sent and forgotten: a failed acknowledgement goes again a little later, a few times (the server
// keeps the first time it hears of each message).
const sendRendered = (sessionId, items, attempt = 0) => {
  postCcRendered(sessionId, items).catch(() => {
    if (attempt + 1 < RENDERED_ATTEMPTS) setTimeout(() => sendRendered(sessionId, items, attempt + 1), retryDelay(attempt))
  })
}

// One message: the student's (their question, or their answer to a card) on the right as in the
// regular chat; an AI role's on the left under its icon and name, rendered as Markdown (AiMarkdown).
function CcMessage({ message, studentName }) {
  if (message.role === 'notice') return <div className="cc-notice-line">{message.text}</div>
  const isStudent = message.role === 'student' || message.role === 'contribution'
  return (
    <div className={`ws-message ${isStudent ? 'ws-owner' : 'cc-role'}`}>
      <div className="ws-message-info">
        {isStudent ? <img src={defaultAvatar} alt="user avatar" /> : <RoleAvatar role={message.role} />}
        <span className="ws-info-name">{isStudent ? studentName : ROLE_LABELS[message.role]}</span>
        <span className="ws-info-time">{formatTime(message.at)}</span>
        {message.role === 'contribution' && <span className="ws-info-time">Prediction</span>}
      </div>
      <div className="ws-message-content-wrapper">
        <div className="ws-message-content">
          {isStudent ? (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{message.text}</pre>
          ) : (
            <AiMarkdown text={message.text} />
          )}
        </div>
      </div>
    </div>
  )
}

// sessionId: the condition's session. currentUserName: the name on the student's own messages.
// maxWords: the study's word limit (1000) for a question or an answer, as the regular chat's.
export default function CcChat({ sessionId, currentUserName = 'Student', maxWords = null }) {
  const { logTelemetryEvent } = useContext(TelemetryContext) || {}
  const { codeRef } = useContext(CodeContext) || {}
  const terminal = useContext(TerminalContext) || {}

  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [disclosure, setDisclosure] = useState(CC_DISCLOSURE)
  const [messages, setMessages] = useState([])
  // The waiting card: { roundId, prompt }, only ever from the server.
  const [pending, setPending] = useState(null)
  // The call under way: 'coach' (a question), 'checker' (an answer to the card, or the checker
  // again), 'cancel' - or null.
  const [busy, setBusy] = useState(null)
  // The last call's failure: { text, attempt, retryOf } (attempt: what Try again sends; none for a
  // refusal no resend changes).
  const [failure, setFailure] = useState(null)
  // The student's latest round while the server is still answering it, from its state
  // (answeringOf): { roundId, role, sure }, or null.
  const [answering, setAnswering] = useState(null)
  // Why the chat takes no more messages, or null.
  const [closed, setClosed] = useState(null)
  const [chatInput, setChatInput] = useState('')
  const [cardDraft, setCardDraft] = useState('')

  const containerRef = useRef(null)
  const historyRef = useRef(null)
  // One call at a time, held at once (state would let a double click through), and how many have
  // started (lookAgain).
  const busyRef = useRef(false)
  const callsRef = useRef(0)
  const closedRef = useRef(null)
  closedRef.current = closed
  const mountedRef = useRef(true)
  // The next look at a round the server is still answering (followUp).
  const followRef = useRef(null)
  // A call ended without the page learning all the server made of it (no answer came, or the
  // server was still answering a round): the server's state may hold what this page never showed.
  const staleRef = useRef(false)
  // What calls made later (Try again, timers) read: the values of the latest render.
  const latest = useRef({})
  latest.current = { sessionId, codeRef, terminal, logTelemetryEvent }
  // Message and card ids acknowledged as shown on this page, and those that came as replies to its
  // own calls (any other came with the server's state: restored).
  const ackedRef = useRef(new Set())
  const repliedRef = useRef(new Set())

  useRegion(REGIONS.CHAT_WINDOW, containerRef, 10)

  const log = (type, payload) => latest.current.logTelemetryEvent?.(type, payload)

  // The code and latest run as they are at this moment (ccContext.js).
  const currentContext = () => {
    const { codeRef: code, terminal: run } = latest.current
    return buildCcContext({
      code: code?.current ?? '',
      lastRunInfo: run.lastRunInfo,
      testResults: run.testResults,
      testOutcomes: run.testOutcomes,
      terminalOutput: run.terminalOutput,
      isRunning: run.isRunning,
    })
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // No more looks at a round the server is answering: a call of the page's own says more (run), or
  // the chat has closed.
  const stopFollowing = () => {
    clearTimeout(followRef.current)
    followRef.current = null
    setAnswering(null)
  }

  const close = (text) => {
    setClosed(text)
    setPending(null)
    setFailure(null)
    stopFollowing()
  }

  // The server's state on screen, which the page is now in step with. A latest round whose
  // Evidence Checker did not answer shows as it did on the page that saw it fail: its notice is the
  // failure below the conversation, with Try again for the checker.
  const applyState = (state) => {
    const failedChecker = failedCheckerOf(state)
    const shown = failedChecker ? state.messages.filter((message) => message.id !== failedChecker.noticeId) : state.messages
    staleRef.current = false
    setDisclosure(state.disclosure || CC_DISCLOSURE)
    setMessages((current) => (sameMessages(current, shown) ? current : shown))
    setPending((current) => (sameCard(current, state.pending) ? current : state.pending))
    if (failedChecker) {
      setFailure((current) =>
        current?.attempt?.stage === 'checker' && current.attempt.roundId === failedChecker.roundId
          ? current
          : {
              text: failedChecker.text,
              attempt: { stage: 'checker', roundId: failedChecker.roundId, clientRequestId: newClientRequestId() },
              retryOf: null,
            }
      )
    }
    if (state.codingOver) close(CODING_OVER_TEXT)
  }

  // After the server's state is shown: while the server is still answering the student's latest
  // round (answeringOf), the page looks again on retryDelay's schedule, FOLLOW_ATTEMPTS times at
  // most, so a round a lost server left unanswered is not waited for for ever. A round it is sure
  // of shows its role thinking and holds the chat box, as a call of the page's own does; one it is
  // not sure of is looked at quietly. attempt: the looks so far.
  const followUp = (state, attempt) => {
    clearTimeout(followRef.current)
    followRef.current = null
    const next = state ? answeringOf(state) : null
    if (!next || attempt >= FOLLOW_ATTEMPTS) {
      setAnswering(null)
      return
    }
    setAnswering((current) => (sameAnswering(current, next) ? current : next))
    followRef.current = setTimeout(() => lookAgain(attempt + 1, state), retryDelay(attempt))
  }

  // How things stand on the server: after a call it refused because its round had moved on (a
  // card waiting that this page did not show, a round answered or closed elsewhere), after the
  // first call that succeeds once the page has fallen behind (staleRef), and while the server is
  // answering (followUp; previous: the state that showed it). A call the student starts meanwhile
  // wins: this older picture would hide its reply. A look that fails leaves what is shown, unless
  // it says the chat is closed.
  const lookAgain = async (attempt = 0, previous = null) => {
    const calls = callsRef.current
    let state
    try {
      state = await getCcState(latest.current.sessionId)
    } catch (error) {
      if (!mountedRef.current || callsRef.current !== calls || closedRef.current) return
      const closing = closingText(ccFailureOf(error), 'state')
      if (closing) close(closing)
      else followUp(previous, attempt)
      return
    }
    if (!mountedRef.current || callsRef.current !== calls || busyRef.current || closedRef.current) return
    applyState(state)
    followUp(state, attempt)
  }

  // The conversation so far and any waiting card, on load (a reload restores both). Tried again
  // until the server answers, as the probes are; until then nothing can be sent.
  useEffect(() => {
    if (sessionId == null) return undefined
    let cancelled = false
    let timer = null
    const load = async (attempt) => {
      try {
        const state = await getCcState(sessionId)
        if (cancelled) return
        applyState(state)
        setLoaded(true)
        followUp(state, 0)
      } catch (error) {
        if (cancelled) return
        const failed = ccFailureOf(error)
        log(CC_EVENTS.FAILED, { stage: 'state', roundId: null, clientRequestId: null, status: failed.status, reason: failed.reason })
        const closing = closingText(failed, 'state')
        if (closing) {
          close(closing)
          setLoaded(true)
          return
        }
        setLoadFailed(true)
        timer = setTimeout(() => load(attempt + 1), retryDelay(attempt))
      }
    }
    load(0)
    return () => {
      cancelled = true
      clearTimeout(timer)
      stopFollowing()
    }
    // Once per session; the helpers read the latest render's values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  const send = (attempt) => {
    const { sessionId: id } = latest.current
    const context = currentContext()
    const { clientRequestId, roundId } = attempt
    if (attempt.stage === 'request') return postCcRequest(id, { clientRequestId, text: attempt.text, context })
    if (attempt.stage === 'contribution') {
      return postCcContribution(id, roundId, { clientRequestId, action: attempt.action, text: attempt.text, context })
    }
    if (attempt.stage === 'checker') return postCcChecker(id, roundId, { clientRequestId, context })
    return postCcCancel(id, roundId)
  }

  // The Code Coach's message came, the Evidence Checker's did not: Try again asks for the checker
  // alone, as a call of its own.
  const offerChecker = (roundId, reportedBy) => {
    log(CC_EVENTS.FAILED, { stage: 'checker', roundId, clientRequestId: reportedBy, status: 200, reason: 'checker-failed' })
    setFailure({ text: FAILED_TEXT, attempt: { stage: 'checker', roundId, clientRequestId: newClientRequestId() }, retryOf: reportedBy })
  }

  const succeeded = (attempt, reply) => {
    if (attempt.stage === 'cancel') {
      setPending(null)
      setCardDraft('')
    } else {
      const roundId = reply.round?.id || attempt.roundId || null
      reply.messages.forEach((message) => repliedRef.current.add(message.id))
      if (reply.pending) repliedRef.current.add(promptId(reply.pending.roundId))
      setMessages((list) => {
        const named =
          attempt.localId && roundId
            ? rekey(list, attempt.localId, `${roundId}:${attempt.stage === 'request' ? 'student' : 'contribution'}`, roundId)
            : list
        return mergeMessages(named, reply.messages)
      })
      // A new question's reply alone can bring a card (a mediate round within its opportunities).
      setPending(attempt.stage === 'request' ? reply.pending : null)
      if (attempt.stage === 'request') setCardDraft('')
      if (reply.checkerFailed && roundId) offerChecker(roundId, attempt.clientRequestId)
    }
    // The server answers again after the page fell behind it: the reply of a call that never came
    // back, or of the round the server was still answering, is in its state, not on this page.
    if (staleRef.current) lookAgain()
  }

  // A card's answer or skip with no reply to say what became of it: the server may have it (the
  // reply lost on its way back), so the page asks how things stand while the chat still waits. An
  // answer the server has shows from its state, the checker's reply with it or as it comes; one it
  // does not have goes back where the student wrote it, with the card, so that the chat box still
  // answers the card and Submit response sends it again. When no state comes either, the page goes
  // by the second: an answer the server had is then refused as no longer pending, which shows it.
  const recheckAnswer = async (attempt, takeBack) => {
    let state = null
    try {
      state = await getCcState(latest.current.sessionId)
    } catch (error) {
      // As if the server did not have it.
    }
    if (!mountedRef.current) return
    const { roundId } = attempt.card
    if (state && state.pending?.roundId !== roundId) {
      // Taken - or the round ended without it (the coding time over, a new question started on
      // another tab), and what the student wrote goes back.
      applyState(state)
      followUp(state, 0)
      if (attempt.action === 'submit' && !state.messages.some((message) => message.id === `${roundId}:contribution`)) takeBack()
      return
    }
    if (state) applyState(state)
    else setPending(attempt.card)
    takeBack()
    setFailure({ text: attempt.action === 'submit' ? ANSWER_FAILED_TEXT : FAILED_TEXT, attempt: null, retryOf: null })
  }

  const failed = async (attempt, error) => {
    const failure = ccFailureOf(error)
    log(CC_EVENTS.FAILED, {
      stage: attempt.stage,
      roundId: attempt.roundId ?? null,
      clientRequestId: attempt.clientRequestId ?? null,
      status: failure.status,
      reason: failure.reason,
    })
    const closing = closingText(failure, attempt.stage)
    if (closing) {
      close(closing)
      return
    }
    // Taken back: what the student wrote returns to where they wrote it, and the page's copy of it
    // goes. A card it answered comes back too.
    const takeBack = () => {
      if (attempt.localId) setMessages((list) => list.filter((message) => message.id !== attempt.localId))
      if (attempt.stage === 'request' || (attempt.stage === 'contribution' && attempt.via === 'chat')) {
        setChatInput((current) => current || attempt.text || '')
      }
      if (attempt.stage === 'contribution' && attempt.via === 'card') setCardDraft((current) => current || attempt.text || '')
    }
    // A message the server refuses as it is, which no resend changes (400; 413: too long): no Try
    // again, and the server's words - but the page's own for one too long, as a message within the
    // word limit can still be too long in characters, and the server's speak of words.
    if (failure.status === 400 || failure.status === 413) {
      takeBack()
      if (attempt.stage === 'contribution') setPending(attempt.card)
      setFailure({ text: failure.status === 413 ? TOO_LONG_TEXT : failure.message || REFUSED_TEXT, attempt: null, retryOf: null })
      return
    }
    // The server's round has moved on from what this page shows: a card waits that this page does
    // not show (another tab, or a reply that never arrived), or the round was answered, cancelled
    // or closed elsewhere (or is gone: 404). Nothing of this call was taken; the page shows how
    // things stand.
    const movedOn =
      (failure.status === 409 && failure.reason !== 'round-busy' && failure.reason !== 'not-started') ||
      failure.status === 404
    if (movedOn) {
      takeBack()
      if (failure.pending) setPending(failure.pending)
      // A new question refused because a card waits: its text is back in the box, where it would
      // now answer the card, so the chat says so.
      if (attempt.stage === 'request' && failure.reason === 'round-pending') {
        setFailure({ text: WAITING_CARD_TEXT, attempt: null, retryOf: null })
      }
      lookAgain()
      return
    }
    // No answer came, the server was still answering a round, or it failed in a way that does not
    // say what it did: the page may be behind the server from here on.
    if (failure.status === null || failure.reason === 'round-busy' || (failure.status >= 500 && failure.reason !== 'cc-unavailable')) {
      staleRef.current = true
    }
    if (attempt.stage === 'contribution') {
      await recheckAnswer(attempt, takeBack)
      return
    }
    // Otherwise Try again: the same call under the same id. The server answers a resent request
    // with what it stored for it (round-busy while it is still answering it), and asks the model
    // again for one that failed (502 cc-unavailable: nothing was delivered, nothing counted), on
    // the same round. The checker after a checkerFailed reply is offered as a call of its own
    // (offerChecker).
    const text =
      failure.reason === 'round-busy'
        ? BUSY_TEXT
        : failure.reason === 'not-started'
          ? NOT_STARTED_TEXT
          : attempt.stage === 'cancel'
            ? CANCEL_FAILED_TEXT
            : failure.message || FAILED_TEXT
    setFailure({ text, attempt, retryOf: attempt.clientRequestId ?? null })
  }

  // One call to the tutor, and what the chat shows after it. attempt: { stage: 'request' |
  // 'contribution' | 'checker' | 'cancel', clientRequestId, roundId, text, action, via, card,
  // localId }.
  const run = async (attempt) => {
    if (busyRef.current || closedRef.current) return
    busyRef.current = true
    callsRef.current += 1
    // Its reply says more than a look at the server's state could.
    stopFollowing()
    setBusy(attempt.stage === 'request' ? 'coach' : attempt.stage === 'cancel' ? 'cancel' : 'checker')
    setFailure(null)
    try {
      const reply = await send(attempt)
      if (mountedRef.current) succeeded(attempt, reply)
    } catch (error) {
      if (mountedRef.current) await failed(attempt, error)
    } finally {
      busyRef.current = false
      if (mountedRef.current) setBusy(null)
    }
  }

  const canSend = () => !busyRef.current && !closedRef.current

  const sendRequest = (text) => {
    if (!canSend()) return
    const clientRequestId = newClientRequestId()
    const context = currentContext()
    const id = localId(clientRequestId)
    setMessages((list) => [...list, { id, roundId: null, role: 'student', text, at: new Date().toISOString() }])
    log(CC_EVENTS.REQUEST_SENT, {
      clientRequestId,
      text,
      length: text.length,
      codeHash: context.codeHash,
      runId: context.run?.runId ?? null,
    })
    run({ stage: 'request', clientRequestId, text, localId: id })
  }

  // The card's answer (action 'submit', via its box or the chat box) or its skip. The card goes at
  // once and the answer shows as the student's message; the Evidence Checker answers in the same call.
  const sendContribution = (action, text, via) => {
    if (!canSend() || !pending) return
    const card = pending
    const clientRequestId = newClientRequestId()
    const context = currentContext()
    const evidence = { codeHash: context.codeHash, runId: context.run?.runId ?? null }
    let id = null
    if (action === 'submit') {
      id = localId(clientRequestId)
      setMessages((list) => [...list, { id, roundId: card.roundId, role: 'contribution', text, at: new Date().toISOString() }])
      log(CC_EVENTS.CONTRIBUTION_SUBMITTED, { roundId: card.roundId, clientRequestId, text, length: text.length, via, ...evidence })
    } else {
      log(CC_EVENTS.SKIPPED, { roundId: card.roundId, clientRequestId, ...evidence })
    }
    setPending(null)
    setCardDraft('')
    run({
      stage: 'contribution',
      clientRequestId,
      roundId: card.roundId,
      action,
      text: action === 'submit' ? text : undefined,
      via,
      card,
      localId: id,
    })
  }

  // "Start a new question": the card's round is cancelled; the chat box then asks something new.
  const startNewQuestion = () => {
    if (!canSend() || !pending) return
    log(CC_EVENTS.ROUND_CANCELLED, { roundId: pending.roundId })
    run({ stage: 'cancel', clientRequestId: null, roundId: pending.roundId })
  }

  const tryAgain = () => {
    if (!failure?.attempt || !canSend()) return
    const { attempt, retryOf } = failure
    log(CC_EVENTS.RETRY, { stage: attempt.stage, roundId: attempt.roundId ?? null, clientRequestId: attempt.clientRequestId, retryOf })
    run(attempt)
  }

  // Render acknowledgements: each coach and checker message, and the card, once it has been on
  // screen - after the browser's next frame, which a hidden tab does not draw until it is shown.
  // Logged here too, with the page's own time.
  useEffect(() => {
    const items = messages
      .filter((message) => (message.role === 'coach' || message.role === 'checker') && !message.id.startsWith(LOCAL))
      .map((message) => ({ messageId: message.id, kind: message.role, roundId: message.roundId, text: message.text }))
    if (pending) items.push({ messageId: promptId(pending.roundId), kind: 'prompt', roundId: pending.roundId, text: pending.prompt })
    const unseen = items.filter((item) => !ackedRef.current.has(item.messageId))
    if (!unseen.length || typeof requestAnimationFrame !== 'function') return undefined
    const frame = requestAnimationFrame(() => {
      unseen.forEach((item) => {
        ackedRef.current.add(item.messageId)
        const restored = !repliedRef.current.has(item.messageId)
        if (item.kind === 'prompt') {
          log(CC_EVENTS.CARD_SHOWN, { roundId: item.roundId, messageId: item.messageId, prompt: item.text, restored })
        } else {
          log(CC_EVENTS.MESSAGE_RENDERED, {
            roundId: item.roundId,
            messageId: item.messageId,
            role: item.kind,
            text: item.text,
            restored,
          })
        }
      })
      sendRendered(latest.current.sessionId, unseen.map(({ messageId, kind }) => ({ messageId, kind })))
    })
    return () => cancelAnimationFrame(frame)
    // log reads the latest render's values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, pending])

  // The role the server's state shows surely at work on the student's latest round (answeringOf):
  // shown thinking, as for a call of the page's own. A round it is not sure of shows nothing.
  const answeringRole = answering?.sure ? answering.role : null

  // The newest message, card or notice in view, as the regular chat scrolls.
  useEffect(() => {
    if (historyRef.current) historyRef.current.scrollTop = historyRef.current.scrollHeight
  }, [messages, pending, busy, answeringRole, failure])

  // Selecting text in the history, as ChatWindow logs it (the card's box logs its own).
  useEffect(() => {
    const el = historyRef.current
    if (!el) return undefined
    let lastSelection = ''
    const onSelection = (event) => {
      if (event.target?.tagName === 'TEXTAREA' || event.target?.tagName === 'INPUT') return
      const selected = window.getSelection().toString()
      if (selected && selected !== lastSelection) {
        lastSelection = selected
        log(CHAT_EVENTS.SELECT_HISTORY, { selected_text: selected, timestamp: Date.now() })
      } else if (!selected) {
        lastSelection = ''
      }
    }
    el.addEventListener('mouseup', onSelection)
    el.addEventListener('keyup', onSelection)
    return () => {
      el.removeEventListener('mouseup', onSelection)
      el.removeEventListener('keyup', onSelection)
    }
    // log reads the latest render's values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleHistoryCopy = (event) => {
    if (event.target?.tagName === 'TEXTAREA' || event.target?.tagName === 'INPUT') return
    const selected = window.getSelection().toString()
    if (selected) log(CHAT_EVENTS.COPY_HISTORY, { selected_text: selected, timestamp: Date.now() })
  }

  const input = useChatInputTelemetry(chatInput, logTelemetryEvent)

  // The word limit, as the regular chat has it: a longer draft stays in the box, Send does nothing,
  // and a note says why; the counter shows past 80% of the limit.
  const wordLimit = Number(maxWords) > 0 ? Number(maxWords) : null
  const wordCount = wordLimit ? countWords(chatInput) : 0
  const isOverWordLimit = wordLimit !== null && wordCount > wordLimit
  const showsWordCount = wordLimit !== null && wordCount > wordLimit * 0.8

  // The tutor is at work: a call of this page's, or a round the server's state shows it surely
  // still answering (after a reload, say), which a new question would only be refused for.
  const waiting = Boolean(busy) || Boolean(answeringRole)
  const inputDisabled = !loaded || waiting || Boolean(closed)
  const canSubmit = !inputDisabled && Boolean(chatInput.trim()) && !isOverWordLimit
  // While a card waits, the chat box answers it.
  const placeholder = closed
    ? 'The tutor is closed'
    : !loaded
      ? 'Loading...'
      : waiting
        ? 'Waiting for the tutor...'
        : pending
          ? 'Answer the prediction above...'
          : 'Ask a question...'

  const handleSubmit = (event) => {
    event.preventDefault()
    if (!canSubmit || busyRef.current) return
    const text = chatInput
    setChatInput('')
    if (pending) sendContribution('submit', text, 'chat')
    else sendRequest(text)
  }

  const thinkingRole = busy === 'coach' || busy === 'checker' ? busy : answeringRole || null

  return (
    <div ref={containerRef} className="ws-chat-container cc-chat">
      <div className="ws-chat">
        <div className="cc-disclosure">{disclosure}</div>

        <div className="ws-chat-history" ref={historyRef} onCopy={handleHistoryCopy}>
          {!loaded && (
            <div className="cc-status">{loadFailed ? 'Could not reach the tutor. Trying again...' : 'Loading the conversation...'}</div>
          )}

          {messages.map((message) => (
            <CcMessage key={message.id} message={message} studentName={currentUserName} />
          ))}

          {pending && !closed && (
            <ParticipationCard
              prompt={pending.prompt}
              draft={cardDraft}
              onDraftChange={setCardDraft}
              onSubmit={(text) => sendContribution('submit', text, 'card')}
              onSkip={() => sendContribution('skip', '', 'card')}
              busy={Boolean(busy)}
              maxWords={maxWords}
            />
          )}

          {thinkingRole && (
            <div className="ws-message cc-role">
              <div className="ws-message-info">
                <RoleAvatar role={thinkingRole} />
                <span className="ws-info-name">{ROLE_LABELS[thinkingRole]}</span>
              </div>
              <div className="ws-message-content-wrapper">
                <div className="ws-message-content" style={{ color: '#888', fontStyle: 'italic', marginTop: '28px' }}>
                  Thinking...
                </div>
              </div>
            </div>
          )}

          {failure && !busy && (
            <div className="cc-notice" role="status">
              <span>{failure.text}</span>
              {failure.attempt && (
                <button type="button" className="cc-button" onClick={tryAgain}>
                  Try again
                </button>
              )}
            </div>
          )}
        </div>

        {closed ? (
          <div className="cc-closed" role="status">
            {closed}
          </div>
        ) : (
          pending && <PendingLine busy={Boolean(busy)} onStartNew={startNewQuestion} />
        )}

        <form className="ws-message-form" onSubmit={handleSubmit}>
          <textarea
            className="ws-message-input"
            aria-label={pending ? 'Your answer to the prediction' : 'Your question'}
            placeholder={placeholder}
            rows={1}
            value={chatInput}
            onChange={(event) => {
              input.onChange(event)
              setChatInput(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) handleSubmit(event)
              input.onKeyDown(event)
            }}
            onSelect={input.onSelect}
            onPaste={input.onPaste}
            onCopy={input.onCopy}
            disabled={inputDisabled}
          />
          {showsWordCount && (
            <div className="ws-char-count">
              <span style={{ color: isOverWordLimit ? '#d32f2f' : '#999', fontWeight: isOverWordLimit ? 'bold' : 'normal' }}>
                {`${formatCount(wordCount)}/${formatCount(wordLimit)} words`}
              </span>
            </div>
          )}
          <button type="submit" className="ws-send-button" disabled={!canSubmit} style={{ opacity: canSubmit ? 1 : 0.5 }}>
            Send
          </button>
        </form>

        {isOverWordLimit && (
          <div style={{ flexShrink: 0, margin: '0 12px 10px', fontSize: '0.78rem', color: '#991b1b' }}>
            {`${formatCount(wordCount)} words - the limit is ${formatCount(wordLimit)}. Shorten your message to send it.`}
          </div>
        )}
      </div>
    </div>
  )
}
